// Contracts: files in the private `contracts` bucket under deals/<deal_id>/, read as text by the Worker
// (/api/contract_text), then AI (task extract_milestones) proposes milestones and invoices for review.
import type { Identifier } from "ra-core";
import { z } from "zod";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { UNTRUSTED_NOTE, untrusted } from "./ai";

export const CONTRACTS_BUCKET = "contracts";
export const CONTRACT_TEXT_ENDPOINT = "/api/contract_text";
export const MAX_CONTRACT_BYTES = 10 * 1024 * 1024;
export const CONTRACT_ACCEPT = ".pdf,.docx,.txt";
const MAX_CONTRACT_CHARS = 45_000; // the AI prompt cap is 60k characters

export const contractFolder = (dealId: Identifier) => `deals/${dealId}`;

/** A storage-safe object name: the upload time, then the file name without odd characters */
export const contractPath = (dealId: Identifier, fileName: string) =>
  `${contractFolder(dealId)}/${Date.now()}-${
    fileName
      .normalize("NFKD")
      .replace(/[^\w.-]+/g, "_")
      .replace(/^[._]+/, "")
      .slice(-100) || "contract"
  }`;

/** The file's text, converted by the Worker (PDF and DOCX via Workers AI toMarkdown) */
export const contractText = async (file: Blob, name: string) => {
  const { data } = await getSupabaseClient().auth.getSession();
  const response = await fetch(CONTRACT_TEXT_ENDPOINT, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${data.session?.access_token ?? ""}`,
      "X-File-Name": name,
    },
    body: file,
    signal: AbortSignal.timeout(90_000),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error(body?.error ?? `Reading failed (${response.status})`);
  return String(body.text ?? "");
};

const isoDate = z.preprocess(
  (value) =>
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())
      ? value.trim()
      : null,
  z.string().nullable(),
);
const label = z.preprocess(
  (value) => (typeof value === "string" ? value.trim().slice(0, 120) : value),
  z.string().min(1),
);
const optionalText = z.preprocess(
  (value) =>
    typeof value === "string" && value.trim()
      ? value.trim().slice(0, 60)
      : null,
  z.string().nullable(),
);
const amount = z.preprocess(
  (value) =>
    typeof value === "string" ? Number(value.replace(/[^\d.]/g, "")) : value,
  z.number().positive().max(1e10),
);

export const ExtractMilestonesSchema = z.object({
  milestones: z
    .array(z.object({ name: label, due_date: isoDate.optional() }))
    .max(30)
    .default([]),
  invoices: z
    .array(
      z.object({
        number: optionalText.optional(),
        amount,
        currency: optionalText.optional(),
        due_date: isoDate.optional(),
        milestone_name: optionalText.optional(),
      }),
    )
    .max(50)
    .default([]),
});
export type ExtractedContract = z.infer<typeof ExtractMilestonesSchema>;

export const buildMilestonesPrompt = (dealName: string, text: string) =>
  [
    `Read this contract or proposal for the deal "${dealName}" and list its delivery milestones and its invoice (payment) schedule.`,
    "Milestones: the phases or deliverables in order, each with a short name and its due date if the text gives one.",
    'Do not include "Not started" or "Completed": the CRM adds those itself.',
    "Invoices: each payment with its amount as a number, the currency as a 3-letter code, its due date if given, its invoice number if given, and the name of the milestone it is tied to (exactly as in your milestone list) if any.",
    "Dates as YYYY-MM-DD; use null when the text does not say. Never invent amounts.",
    'Answer with JSON only: {"milestones": [{"name": "...", "due_date": "2026-11-30"}], "invoices": [{"number": null, "amount": 25000, "currency": "EGP", "due_date": null, "milestone_name": "..."}]}',
    "",
    UNTRUSTED_NOTE,
    untrusted("contract", text.slice(0, MAX_CONTRACT_CHARS)),
    text.length > MAX_CONTRACT_CHARS
      ? "(The contract was cut short; list only what the text above shows.)"
      : "",
  ]
    .filter(Boolean)
    .join("\n");

/** One row of the review table */
export type MilestoneDraft = { name: string; due_date: string | null };
export type InvoiceDraft = {
  number: string;
  amount: number;
  currency: string;
  due_date: string | null;
  milestone_name: string | null;
};

const FIXED = ["not started", "completed"];
const key = (name: string) => name.trim().toLowerCase();

/** The AI answer as editable rows: fixed milestones dropped, duplicates merged, invoice numbers filled in */
export const draftsFrom = (
  answer: ExtractedContract,
  dealId: Identifier,
  defaultCurrency = "EGP",
) => {
  const milestones: MilestoneDraft[] = [];
  for (const m of answer.milestones) {
    if (FIXED.includes(key(m.name))) continue;
    if (milestones.some((d) => key(d.name) === key(m.name))) continue;
    milestones.push({ name: m.name, due_date: m.due_date ?? null });
  }
  const invoices: InvoiceDraft[] = answer.invoices.map((inv, i) => ({
    // a bare "1" or "2" from the contract would clash across deals: prefix it with the deal
    number: /[a-z]/i.test(inv.number ?? "")
      ? inv.number!
      : `INV-${dealId}-${inv.number ?? i + 1}`,
    amount: Math.round(inv.amount * 100) / 100,
    currency: (inv.currency ?? defaultCurrency).toUpperCase().slice(0, 3),
    due_date: inv.due_date ?? null,
    milestone_name:
      milestones.find((m) => key(m.name) === key(inv.milestone_name ?? ""))
        ?.name ?? null,
  }));
  return { milestones, invoices };
};

type ExistingMilestone = { id: Identifier; name: string; position: number };

/**
 * The set_delivery_milestones list: "Not started" first, the reviewed milestones, "Completed" last.
 * A reviewed milestone whose name matches an existing middle one keeps its id (so linked invoices stay linked);
 * the other middle milestones are removed.
 */
export const milestonePlan = (
  existing: ExistingMilestone[],
  drafts: MilestoneDraft[],
) => {
  const sorted = [...existing].sort((a, b) => a.position - b.position);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (!first || !last || first === last)
    throw new Error("The delivery has no Not started / Completed milestones");
  const middle = sorted.slice(1, -1);
  return [
    { id: first.id },
    ...drafts
      .filter((d) => d.name.trim())
      .map((d) => {
        const same = middle.find((m) => key(m.name) === key(d.name));
        return same
          ? { id: same.id, name: d.name.trim(), due_date: d.due_date }
          : { name: d.name.trim(), due_date: d.due_date };
      }),
    { id: last.id },
  ];
};

/** The invoices to create (status draft), each linked to its milestone by name once the milestones are saved */
export const invoicesToCreate = (
  drafts: InvoiceDraft[],
  milestones: { id: Identifier; name: string }[],
  deal: { id: Identifier; company_id: Identifier },
) =>
  drafts
    .filter((d) => d.number.trim() && d.amount > 0)
    .map((d) => ({
      number: d.number.trim(),
      amount: d.amount,
      currency: d.currency.trim().toUpperCase() || "EGP",
      due_date: d.due_date || null,
      status: "draft" as const,
      deal_id: deal.id,
      company_id: deal.company_id,
      milestone_id:
        milestones.find((m) => key(m.name) === key(d.milestone_name ?? ""))
          ?.id ?? null,
    }));
